'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useAddFavoriteLocationsMutation,
  useDeleteFavoriteLocationsMutation,
  useGetFavoriteLocationsQuery,
  useGetLocationsQuery,
  useGetStatesQuery
} from 'services/locationsApi';

import { Box, useTheme } from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { getAbbrState, groupLocationsByState } from 'utils';

import { ContentFilter, LocationInfoType, LocationInterface } from 'types';

import { Button, Loader, MultiStepHeader } from 'ui';
import { LocationBlock } from '../LocationBlock';
import { LocationInfoBanner } from '../LocationInfoBanner';

interface FavoritesLocationProps {
  onSelectHandler: (item: LocationInterface) => void;
  onBackHandler: () => void;
}

const FavoritesLocation = ({
  onSelectHandler,
  onBackHandler
}: FavoritesLocationProps) => {
  const {
    colors: {
      palette: {
        components: {
          locations: { locationPage: styles }
        }
      }
    }
  } = useTheme();
  const { userInfo } = useAppSelector((state) => state.persistAuth);

  const { data: locations = [], isLoading: isLocationsLoading } =
    useGetLocationsQuery(ContentFilter.ServicesOnly);
  const { data: favoriteLocations, isLoading: isFavLoading } =
    useGetFavoriteLocationsQuery('', {
      skip: !userInfo
    });
  const [addFavLocation] = useAddFavoriteLocationsMutation() || {};
  const [deleteFavLocation] = useDeleteFavoriteLocationsMutation() || {};
  const { data: states = [] } = useGetStatesQuery('') ?? {};
  const [favIds, setFavIds] = useState<number[]>([]);

  const isLoading = isLocationsLoading || isFavLoading;

  const favoriteLocationIds = useMemo(
    () =>
      Array.isArray(favoriteLocations)
        ? favoriteLocations.map((loc) => loc.item_num)
        : [],
    [favoriteLocations]
  );

  const favLocList = locations.filter((loc) => favIds.includes(loc.id));

  const groupedLocations = groupLocationsByState(favLocList);
  const groupedStates = getAbbrState(states);

  useEffect(() => {
    if (favoriteLocations) {
      const ids = favoriteLocations.map(({ item_num }) => item_num);
      setFavIds(ids);
    }
    // don't need favoriteLocations as a dependency, need set list only once if exist
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedLocationsArray = useMemo(
    () =>
      Object.entries(groupedLocations).sort(([stateA], [stateB]) =>
        stateA.localeCompare(stateB)
      ),
    [groupedLocations]
  );

  const onFavoriteIconClick = useCallback(
    (isSelected: boolean, locationId: number) => {
      if (isSelected) {
        deleteFavLocation(locationId);
      } else {
        addFavLocation(locationId);
      }
    },
    [deleteFavLocation, addFavLocation]
  );

  return isLoading ? (
    <Loader absolute />
  ) : (
    <Box mt={{ base: '15px', md: 0 }}>
      {sortedLocationsArray.length ? (
        <>
          <MultiStepHeader
            title='My Favorite Locations'
            subTitle='Please choose the preferred location for your appointment'
            headingColor={styles.headingColor}
            descriptionColor={styles.descriptionColor}
            mt={0}
            mb={{ base: '20px', md: '60px' }}
            descriptionPX={{}}
          />
          {sortedLocationsArray.map(([state, locationItems]) => {
            const isValidLocations =
              state && locationItems.every((item) => item.name && item.city);
            return isValidLocations ? (
              <LocationBlock
                key={state}
                isHidedHeader={false}
                locations={locationItems}
                state={groupedStates[state]}
                onSelectLocation={onSelectHandler}
                onFavoriteIconClick={onFavoriteIconClick}
                favoriteLocationIds={favoriteLocationIds}
                isLogged={!!userInfo}
              />
            ) : null;
          })}
          <Button
            colorScheme='black'
            onClick={onBackHandler}
            margin='50px auto 0'
            w='150px'
            padding={0}
          >
            Back
          </Button>
        </>
      ) : (
        <LocationInfoBanner
          goBack={onBackHandler}
          type={LocationInfoType.NO_FAVORITE_LOCATION}
        />
      )}
    </Box>
  );
};

export default FavoritesLocation;
